import { useEffect, useState } from "react";
import { ClientsDashboard } from "../../../components/leads/ClientsDashboard";
import { ScopeOfWorkView } from "../../../components/leads/ScopeOfWorkView";
import { QuotationView } from "../../../components/leads/QuotationView";
import { LeadView } from "../../../components/leads/LeadView";
import { User, FileText, FileSpreadsheet } from "lucide-react";
import RequireAccess from "components/RequireAccess";
import LoadingScreen from "components/UI_components/LoadingScreen";
import { useLocation } from "react-router-dom";
import { LeadAPI } from "api/lead/service";

const normalizeLeadToClient = (lead) => {
  if (!lead) return null;
  const createdRaw = lead.created_on || lead.createdAt || lead.created_at || "";
  return {
    id: String(lead.id ?? lead.pk ?? ""),
    organization: lead.organization,
    name: lead.name || "",
    phone: lead.phone || "",
    email: lead.email || undefined,
    city: lead.city || "",
    projectType: lead.project_type || lead.projectType || undefined,
    propertySize: lead.property_size || lead.propertySize || undefined,
    budget: lead.budget,
    startDate: lead.start_date || lead.startDate || undefined,
    notes: lead.remarks || lead.notes || undefined,
    status: lead.status || "lead",
    final_budget: lead.final_budget,
    createdAt: createdRaw ? String(createdRaw).split("T")[0] : undefined,
    projectsCount: 0,
  };
};

export default function LeadManagement({ currentMember, permissionsLoaded }) {
  const [selectedClient, setSelectedClient] = useState(null);
  const [activeTab, setActiveTab] = useState("lead");
  const location = useLocation();
  const isOwnerOrAdmin =
    currentMember?.joined_as === "owner" || currentMember?.is_admin === true;
  const hasLeadApprover =
    isOwnerOrAdmin || currentMember?.lead_approver === true;
  const hasLeadAccess =
    isOwnerOrAdmin ||
    currentMember?.lead_control === true ||
    currentMember?.lead_approver === true;
  const [loading, setLoading] = useState(false);

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    if (client.status === "active") {
      setActiveTab("scope");
    } else if (
      client.status === "quotation" ||
      client.status === "quotation_active"
    ) {
      setActiveTab("quotation");
    } else {
      setActiveTab("lead");
    }
  };

  const handleCreateQuotation = (scopeId) => {
    setActiveTab("quotation");
  };

  const handleGoToScope = () => {
    setActiveTab("scope");
  };

  const handleGoToQuotation = () => {
    setActiveTab("quotation");
  };

  const handleLeadStatusChange = (nextStatus, nextLeadData) => {
    setSelectedClient((prev) => {
      if (!prev) return prev;
      const merged = nextLeadData ? { ...prev, ...nextLeadData } : { ...prev };
      if (nextStatus) merged.status = nextStatus;
      return merged;
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const leadId = params.get("leadId");
    const tabParam = params.get("tab");
    const normalizedTab = ["lead", "scope", "quotation"].includes(tabParam)
      ? tabParam
      : null;

    if (normalizedTab) {
      setActiveTab(normalizedTab);
    }

    if (!leadId) return;

    let cancelled = false;
    const orgId = LeadAPI.orgId();

    (async () => {
      if (!orgId) return;
      setLoading(true);
      try {
        const res = await LeadAPI.getLead(orgId, leadId);
        const lead = res?.data || res;
        const mapped = normalizeLeadToClient(lead);
        if (!cancelled && mapped?.id) {
          setSelectedClient(mapped);
          if (normalizedTab) setActiveTab(normalizedTab);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load lead from URL params:", error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.search]);
  if (!hasLeadAccess && permissionsLoaded) {
    return (
      <RequireAccess
        module="Lead Management"
        currentMember={currentMember}
        permission="lead_control"
      />
    );
  }

  if (!selectedClient) {
    return (
      <div className="h-[90vh] overflow-y-scroll p-4 w-[85%]">
        <ClientsDashboard onSelectClient={handleSelectClient} />
      </div>
    );
  }

  const tabs = [
    { id: "lead", label: "Lead View", icon: User },
    { id: "scope", label: "Scope of Work", icon: FileText },
    { id: "quotation", label: "Quotation", icon: FileSpreadsheet },
  ];

  return (
    <div className="h-[92vh] overflow-y-scroll flex flex-col p-4 w-[85%]">
      {/* Tab Navigation - Compact */}
      <div className="flex items-center gap-1 mb-3 border-b border-border">
        <button
          onClick={() => setSelectedClient(null)}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1.5"
        >
          ← Dashboard
        </button>
        <span className="text-xs font-medium text-foreground px-2 py-1 bg-primary/5 rounded">
          {selectedClient.name}
        </span>
        <div className="flex-1" />
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border-b-2 transition-colors -mb-px ${activeTab === tab.id
                ? "border-sm-primary text-sm-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === "lead" && (
          <LeadView
            client={selectedClient}
            onCreateScope={handleGoToScope}
            onCreateQuotation={handleGoToQuotation}
            onDelete={() => setSelectedClient(null)}
          />
        )}
        {activeTab === "scope" && (
          <ScopeOfWorkView
            client={selectedClient}
            onCreateQuotation={handleCreateQuotation}
            canApprove={hasLeadApprover}
            canViewQuotation={hasLeadApprover}
          />
        )}
        {activeTab === "quotation" && (
          <QuotationView
            client={selectedClient}
            canApprove={hasLeadApprover}
            leadStatusGlobal={selectedClient?.status}
            onLeadStatusChange={handleLeadStatusChange}
          />
        )}
      </div>
    </div>
  );
}
